import { AfterViewInit, ChangeDetectorRef, Component, HostListener, input, Input, OnChanges, Optional, Self, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DateTimePickerDirective } from '../calendar/calendar-overlay.directive';
import { FormsModule, NgControl, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ControlBase } from '../base/control.base';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'cec-date-time-picker',
  standalone: true,
  imports: [CommonModule, DateTimePickerDirective, MatButtonModule, MatDatepickerModule, MatLabel, MatFormFieldModule, MatInputModule, FormsModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss', '../shared/input.styles.scss']
})
export class CecDateTimePickerComponent extends ControlBase<Date> implements OnChanges {
  @Input() date: Date = new Date();
  @Input() increaseMins: number | undefined = undefined;

  public moreThan = input<CecDateTimePickerComponent | undefined>(undefined);

  formattedDate = '';

  protected errors: ValidationErrors | undefined = undefined;

  constructor(@Optional() @Self() public ngControl: NgControl, private datepipe: DatePipe, private cdr: ChangeDetectorRef) {
    super(ngControl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['increaseMins'] && this.increaseMins && this.value) {
      this.value.setMinutes(this.value.getMinutes() + this.increaseMins);
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    
    /** if more than component is not assigned, do nothing */
    if (!this.moreThan()) {
      return;
    }

    /** subscribes to more than component value change */
    this.subs.add(this.moreThan().valueChangedDebouncer.subscribe((value: Date) => {
      this.processMoreThan(value);
    }));

    /** subscribes to current value change */
    this.subs.add(this.valueChangedDebouncer.subscribe(() => {
      this.processMoreThan(this.moreThan().ngControl?.control?.value);
    }));
  }

  protected getControlValue(): Date {
    return this.value;
  }

  protected setControlValue(val: Date): void {
    if (!val) {
      return null;
    }

    this.value = val;
    this.formatDate(this.value);
    this.valueChangedDebouncer.next(
      this.value
    );
    this.cdr.detectChanges();
  }

  protected setControlDisabled(isDisable: boolean): void {
    this.isDisabled.set(isDisable);
  }

  private formatDate(value: Date): void {

    var date = new Date(this.datepipe.transform(value, 'yyyy-MM-dd HH:mm'));
    var day = date.getDate();
    var month = date.getMonth() + 1; // Months are zero-indexed
    var year = date.getFullYear();
    var hours = date.getHours();
    var minutes = date.getMinutes();

    // Formatting with leading zeros for day, month, hours, and minutes if needed
    this.formattedDate = `${day}.${month}.${year} ${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
  }

  public override afterFocusOut(control: NgControl) {
    if (this.ngControl !== control) {
      this.processMoreThan(control.control.value);
    } else {
      this.ngControl?.control?.setErrors(this.errors);
    }
  }

  protected processMoreThan(value: Date): void {
    if (new Date(this.value) <= value) {
      this.ngControl?.control?.setErrors({ higher: this.moreThan().labelKey });
      this.ngControl?.control?.markAsTouched();
      this.errors = { higher: this.moreThan().labelKey };
      this.moreThan()?.ngControl?.control?.setErrors({ lower: this.labelKey });
      this.moreThan()?.ngControl?.control?.markAsTouched();
      this.moreThan().errors = { lower: this.labelKey };
    } else {
      this.ngControl?.control?.setErrors({higher: null});
      this.ngControl?.control?.updateValueAndValidity();
      this.errors = undefined;
      this.moreThan()?.ngControl?.control?.setErrors({lower: null});
      this.moreThan()?.ngControl?.control?.updateValueAndValidity();
      this.moreThan().errors = undefined;
    }
    this.errorMessages.update(() => this.getErrorMessages());
    this.moreThan()?.errorMessages.update(() => this.moreThan().getErrorMessages());
  }
}
